import { useState, useEffect, useRef } from "react";
import { Edit, Trash2, Eye, History, Search, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useNationsAdmin } from "@/hooks/api/useNations";
import type { Player, PlayerFilters, Pagination } from "@/types/api.types";
import TablePagination from "@/components/TablePagination";
import { Switch } from "@/components/ui/switch";

interface PlayerTableProps {
  players: Player[];
  isLoading: boolean;
  filters: PlayerFilters;
  onFiltersChange: (filters: PlayerFilters) => void;
  onClearFilters: () => void;
  onEdit: (player: Player) => void;
  onView: (player: Player) => void;
  onDelete: (id: number) => void;
  onToggleActive: (player: Player) => void;
  isDeleting: boolean;
  pagination?: Pagination;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

const POSITIONS = ["GK", "DEF", "MID", "FWD"];

const positionColors: Record<string, string> = {
  GK: "bg-yellow-100 text-yellow-800",
  DEF: "bg-blue-100 text-blue-800",
  MID: "bg-green-100 text-green-800",
  FWD: "bg-red-100 text-red-800",
};

export default function PlayerTable({
  players,
  isLoading,
  filters,
  onFiltersChange,
  onClearFilters,
  onEdit,
  onView,
  onDelete,
  onToggleActive,
  isDeleting,
  pagination,
  onPageChange,
  onPageSizeChange,
}: PlayerTableProps) {
  const { data: nationsData } = useNationsAdmin({ limit: 200 });
  const navigate = useNavigate();

  const [localSearch, setLocalSearch] = useState(filters.search || "");
  const filtersRef = useRef(filters);
  const onFiltersChangeRef = useRef(onFiltersChange);
  const isMounted = useRef(false);
  useEffect(() => { filtersRef.current = filters; }, [filters]);
  useEffect(() => { onFiltersChangeRef.current = onFiltersChange; }, [onFiltersChange]);

  // Sync local search when filters are reset externally (e.g. clear filters)
  useEffect(() => { setLocalSearch(filters.search || ""); }, [filters.search]);

  useEffect(() => {
    if (!isMounted.current) { isMounted.current = true; return; }
    const timer = setTimeout(() => {
      onFiltersChangeRef.current({ ...filtersRef.current, search: localSearch || undefined });
    }, 400);
    return () => clearTimeout(timer);
  }, [localSearch]);

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Filters + Stats */}
      <div className="p-4 border-b flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <Input
              placeholder="Search players..."
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
              className="pl-8 max-w-xs"
            />
          </div>

        <Select
          value={filters.position || "all"}
          onValueChange={(val) =>
            onFiltersChange({
              ...filters,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              position: val === "all" ? undefined : (val as any),
            })
          }
        >
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Position" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Positions</SelectItem>
            {POSITIONS.map((pos) => (
              <SelectItem key={pos} value={pos}>
                {pos}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filters.nationId?.toString() || "all"}
          onValueChange={(val) =>
            onFiltersChange({
              ...filters,
              nationId: val === "all" ? undefined : Number(val),
            })
          }
        >
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Nation" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Nations</SelectItem>
            {nationsData?.nations?.map((nation) => (
              <SelectItem key={nation.id} value={nation.id.toString()}>
                {nation.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={
            filters.isActive === undefined ? "true" : filters.isActive.toString()
          }
          onValueChange={(val) =>
            onFiltersChange({
              ...filters,
              // "all" → send literal 'all' so backend skips the isActive
              // filter; undefined would silently default to active-only.
              isActive: val === "all" ? "all" : val === "true",
            })
          }
        >
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="true">Active</SelectItem>
            <SelectItem value="false">Inactive</SelectItem>
          </SelectContent>
        </Select>

          {(!!filters.search || !!filters.position || filters.nationId !== undefined || filters.isActive === false || filters.isActive === 'all') && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearFilters}
              className="text-gray-500 hover:text-gray-700 gap-1"
            >
              <X className="h-3.5 w-3.5" />
              Clear
            </Button>
          )}
        </div>

        <div className="flex items-center gap-2 text-xs">
          <Badge className="bg-gray-100 text-gray-700 font-medium">{pagination?.total ?? players.length} Total</Badge>
          <Badge className="bg-green-100 text-green-700 font-medium">{players.filter((p) => p.isActive).length} Active</Badge>
          <Badge className="bg-red-100 text-red-700 font-medium">{players.filter((p) => !p.isActive).length} Inactive</Badge>
        </div>
      </div>

      {/* Table */}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Player</TableHead>
            <TableHead>Position</TableHead>
            <TableHead>Nation</TableHead>
            <TableHead>Club</TableHead>
            <TableHead>Points</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center py-16">
                <div className="flex justify-center">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
                </div>
              </TableCell>
            </TableRow>
          ) : players.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                No players found
              </TableCell>
            </TableRow>
          ) : (
            players.map((player) => (
              <TableRow
                key={player.id}
                className={!player.isActive ? "bg-gray-50 opacity-70" : ""}
              >
                <TableCell>
                  <div className="flex items-center gap-3">
                    {player.photo ? (
                      <img
                        src={player.photo}
                        alt={player.name}
                        className="w-10 h-10 rounded-full object-cover border"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-sm font-bold text-gray-500">
                        {player.name.charAt(0)}
                      </div>
                    )}
                    <div>
                      <div className="font-medium">{player.name}</div>
                      {player.jerseyNumber && (
                        <div className="text-xs text-gray-400">
                          #{player.jerseyNumber}
                        </div>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge className={positionColors[player.position] || ""}>
                    {player.position}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {player.nation?.flagUrl && (
                      <img
                        src={player.nation.flagUrl}
                        alt={player.nation.name}
                        className="w-6 h-4 object-cover rounded"
                      />
                    )}
                    <span className="text-sm">
                      {player.nation?.name || "—"}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {player.club?.logoUrl && (
                      <img src={player.club.logoUrl} alt="" className="w-5 h-5 rounded-full object-cover" />
                    )}
                    <span className="text-sm text-gray-600">
                      {player.club?.name || "—"}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <span className="font-semibold text-blue-600">
                    {player.points ?? 0}
                  </span>
                </TableCell>
                <TableCell>
                  {player.isActive ? (
                    <Badge className="bg-green-600 text-white">Active</Badge>
                  ) : (
                    <Badge variant="destructive" className="bg-red-600">
                      Inactive
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onView(player)}
                      title="View"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEdit(player)}
                      title="Edit"
                    >
                      <Edit className="h-4 w-4 text-blue-600" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/players/${player.id}/career`)}
                      title="Career Timeline"
                    >
                      <History className="h-4 w-4 text-purple-600" />
                    </Button>
                    <Switch
                      checked={player.isActive}
                      onCheckedChange={() => onToggleActive(player)}
                      className="
                      mt-1
    data-[state=checked]:bg-green-600
    data-[state=unchecked]:bg-red-500
  "
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete(player.id)}
                      disabled={isDeleting}
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* Pagination */}
      {pagination && (
        <TablePagination
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          totalItems={pagination.total}
          pageSize={pagination.limit}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      )}
    </div>
  );
}
